/**
 * Turn the canvas preview into something you can pick up and look at.
 *
 * A canvas is a physical object with depth, and a flat front-on preview says
 * nothing about that — the weave alone was not reading as "this is stretched
 * cloth on a frame". Tilting it shows the one thing a poster cannot do: a side.
 *
 * The theme already carried a `style-perspective` mode from the sibling brand,
 * but it was a fixed -16° tilt with a plain white edge, and nothing switched it
 * on. This replaces it with a tilt the customer drives, and an edge that
 * continues the artwork round the side the way a real wrap does.
 *
 * Depth is not a constant: an unframed canvas is on a 39 mm bar and a framed
 * one on 18 mm, so the thickness you see is the thickness you get.
 *
 * On-screen only. Nothing here touches .hidden-preview, which is what the print
 * file is rendered from.
 */
(function () {
  var MAX_TILT = 32;          // degrees either side — beyond this it reads as a trick
  var REST_TILT = 0;          // rests square on; the hand below shows it moves
  // The magnifier is off (Aleks, 2026-10-01). A quick click on the picture
  // opens the zoom by itself — a drag is told apart from a click by the few
  // pixels in DRAG_SLOP below, so the two never fight — and a button for
  // something the picture already does was one thing too many in that corner.
  // Everything it needs is still here: set this true to bring it back.
  var SHOW_ZOOM_BUTTON = false;
  // Millimetres of what you actually see edge-on.
  //
  // Unframed, that is the stretcher bar the canvas is wrapped on: 39mm.
  //
  // Framed, it is the frame, not the canvas. The canvas inside is on an 18mm
  // bar, but what stands proud of the wall is the moulding, and Alfredssons'
  // float frame is 45mm deep with a 9mm front face — confirmed by production,
  // where this used to be 18mm plus 9mm of my own guesswork. That guess made
  // the frame read as 27mm, so a frame whose side should be five times its
  // front face was drawing it at under three.
  var BAR_MM = { framed: 45, unframed: 39 };

  var cont, front, sideRight, sideLeft, sideTop, sideBottom, shadow;
  var tilt = 0, dragging = false, startX = 0, startTilt = 0, nudged = false;
  // A click on the preview opens the zoom modal (pp-poster-zoom.js), and a drag
  // ends in a click as far as that listener is concerned. Anything past a few
  // pixels counts as a turn, and the click that follows it is swallowed.
  var DRAG_SLOP = 5;
  var moved = 0;

  function el(cls) {
    var d = document.createElement('div');
    d.className = cls;
    return d;
  }

  function mount() {
    cont = document.querySelector('.petposter-template .bp-poster-preview-cont');
    if (!cont || cont.__pp3d) return false;
    front = cont.querySelector('.bp-poster-preview');
    if (!front) return false;
    cont.__pp3d = true;

    // The ported markup has a right-hand side already; it needs a left one too,
    // because the customer can turn it either way.
    sideRight = cont.querySelector('.bp-canvas-side') || el('bp-canvas-side');
    if (!sideRight.parentNode) cont.appendChild(sideRight);
    sideRight.classList.add('pp-edge', 'pp-edge--right');
    sideLeft = el('bp-canvas-side pp-edge pp-edge--left');
    cont.appendChild(sideLeft);
    // Top and bottom faces close the box. Without them you are looking into an
    // open-ended tube and the corners read as hollow — which is exactly what
    // they looked like.
    sideTop = el('bp-canvas-side pp-edge pp-edge--top');
    sideBottom = el('bp-canvas-side pp-edge pp-edge--bottom');
    cont.appendChild(sideTop);
    cont.appendChild(sideBottom);
    shadow = cont.querySelector('.bp-canvas-shadow') || el('bp-canvas-shadow');
    if (!shadow.parentNode) cont.appendChild(shadow);

    // NOT added here. enabled() owns this class, and adding it on mount meant
    // a paper poster wore it for the moment between mounting and being told
    // which material it is.
    bind();
    if (SHOW_ZOOM_BUTTON) addZoomButton();
    return true;
  }

  // Which float frame is on, if any. Read from the swatch's image filename
  // (frame-dark-oak-canvas.png), not its title — titles are translated, so
  // "Marco flotante roble" would never match anything.
  function currentFrame() {
    var r = document.querySelector('.bp-frame-radio:checked');
    if (!r || r.id === 'frame-opt-0') return null;
    var src = r.getAttribute('data-image') || '';
    var m = src.match(/frame-([a-z-]+?)-canvas\./i);
    // Only a float frame counts. This used to fall back to black for anything
    // it could not name, which made every PAPER frame — frame-black.png and the
    // rest — look like a canvas float frame to the code below, and a poster
    // picked up a moulding-coloured ring and a frame's worth of margin that had
    // nothing to do with it.
    return m ? m[1].toLowerCase() : null;
  }

  // The moulding seen end-on. Two stops rather than a flat fill: a frame edge
  // catches light along its front arris and falls away behind, and without that
  // it reads as a coloured rectangle stuck to the side.
  // The moulding's own colour. The grain multiplies over this and the feather
  // darkens it towards the back, so these are the lit face of each wood.
  var FRAME_EDGE = {
    'oak':      ['#dcc19b'],
    'dark-oak': ['#7b5739'],
    'black':    ['#343434'],
    'white':    ['#f4f2ef'],
  };

  // A soft falloff away from the front face. The first attempt used an inset
  // box-shadow, which lands as a hard band — a real edge darkens gradually as
  // it turns away from the light.
  function feather(right, horizontal) {
    return 'linear-gradient(to ' + (horizontal ? (right ? 'bottom' : 'top') : (right ? 'right' : 'left')) + ', ' +
      'rgba(0,0,0,0) 0%, rgba(0,0,0,.10) 45%, rgba(0,0,0,.26) 100%)';
  }

  // Painting the four faces is the expensive thing here — four background
  // stacks, one of them the full artwork. Nothing about the sides changes
  // unless the frame, the artwork, the background colour or the fill mode
  // does, so anything else that asks for a repaint gets turned away at the
  // door.
  var lastPaint = null;
  var lastRing = null, lastRingAsWritten = null;
  // Whether the canvas preview is on at all. The repaint listeners are bound
  // once, for the life of the page, and a paper poster fires the same events —
  // so without this they ran the canvas painting over a poster.
  var active = false;

  function paintEdges(force) {
    if (!active) return;
    var frame = currentFrame();
    var img = document.querySelector('.petposter-template .bp-illustration-img');
    var src = img && img.getAttribute('src');
    var bg = getComputedStyle(front).backgroundColor;
    // Unframed: what wraps round the bar is whatever reaches the edge of the
    // SHEET, not the edge of the artwork. Only a fill-poster design carries its
    // image all the way out; on a bordered one the wrap is the background
    // colour. Taking the artwork's own edge pixels regardless put a dark brown
    // wrap on a poster with a white border.
    var bleeds = !!document.querySelector('.petposter-template.fill-poster');

    // Hand the frame layer its picture and its real width. Both have to go
    // through here rather than through the picker's own background-image,
    // because a border image is the only way to make the moulding a size
    // instead of a percentage.
    var checked = document.querySelector('.bp-frame-radio:checked');
    var frameSrc = frame && checked && checked.getAttribute('data-image');
    var band = frameBandPx(frame);
    var fill = FRAME_FILL[frame] || FRAME_FILL.black;
    cont.style.setProperty('--pp-frame-band', band.toFixed(1) + 'px');
    cont.style.setProperty('--pp-frame-src', frameSrc ? 'url("' + frameSrc + '")' : 'none');
    // A ring of what the frame is made of, behind the frame.
    //
    // Chrome draws a border image as nine separate pieces and leaves a hairline
    // of whatever is behind it between them. On a light page that is a pale
    // line along every slice boundary: down the inside of each rail, and
    // running out from the corners of the canvas. Proved, not guessed — a flat
    // border colour on the same element at the same size has no pieces and
    // showed no lines, and pinning the border to a whole number of pixels
    // changed nothing.
    //
    // The ring reaches the frame's outer edge and no further; inside it, the
    // gap's share of the band, which is what the asset puts there. A seam then
    // shows the thing it is sitting in the middle of instead of the page.
    //
    // Written onto the element rather than declared in CSS: as a rule it was
    // ignored outright — more specific than the theme's own shadow on this
    // element, matching, parsed, and changing its inputs moved the computed
    // style not at all. The last shadow is the preview's own, carried along so
    // nothing is lost by overwriting the property.
    var ring = frame
      ? '0 0 0 ' + (band * (SRC_BAND - SRC_WOOD) / SRC_BAND).toFixed(1) + 'px ' + fill[1] +
        ',0 0 0 ' + Math.max(0, band - 2).toFixed(1) + 'px ' + fill[0] +
        ',0 0 20px -5px #adadad'
      : '';
    // Rewritten whenever it is not what we left there. The theme's own frame
    // handler sets box-shadow on this very element on every frame change —
    // `none` when a frame is picked — so the ring was being wiped and, because
    // the signature below had not changed, never put back. The seams came
    // straight back with it.
    //
    // Two values are kept: what we asked for, and what the browser spelled it
    // as. Comparing against the browser's own spelling is what makes "has
    // something else touched this?" answerable without writing every time.
    if (ring !== lastRing || front.style.boxShadow !== lastRingAsWritten) {
      lastRing = ring;
      front.style.boxShadow = ring;
      lastRingAsWritten = front.style.boxShadow;
    }

    // Room under the frame for the line of text below the preview. That line is
    // placed for a bare poster, so a framed canvas — which stands nineteen
    // pixels proud of the canvas at 30x40 — came down on top of it. Pushing it
    // by the overhang keeps the gap the design already had, whatever the frame
    // and the size work out to.
    // Taken on the preview rather than on the text: .bp-poster-cont is a flex
    // column, and a margin on the text itself moved it by under a pixel.
    cont.style.marginBottom = frame ? Math.max(0, band - 2).toFixed(1) + 'px' : '';
    placeZoomButton();

    // Only the four faces are gated. Everything above is cheap and has to be
    // able to repair itself after another script writes over it; repainting
    // four background stacks, one of them the whole artwork, does not.
    var sig = [frame, src, bg, bleeds, Math.round(pxPerMm() * 100)].join('\n');
    if (!force && sig === lastPaint) return;
    lastPaint = sig;

    [sideLeft, sideRight, sideTop, sideBottom].forEach(function (side) {
      if (!side) return;
      var horizontal = side === sideTop || side === sideBottom;
      // The top and bottom run along the other axis, so their shading and their
      // slice of the frame come from that direction instead.
      var right = side === sideRight || side === sideBottom;
      side.classList.toggle('pp-edge--framed', !!frame);

      if (frame) {
        // Framed: the side carries the REAL moulding, cut from the same
        // photograph as the frame on the front — frame-<colour>-side.png is the
        // straight run of that wood, away from the mitres and clear of the float
        // gap. The generated grain it replaces was convincing until you looked:
        // one of its fibres landed as a bright line down the middle of the edge.
        var r = document.querySelector('.bp-frame-radio:checked');
        var frameSrc = r && r.getAttribute('data-image');
        var sideSrc = frameSrc && frameSrc.replace(/-canvas\.png(\?|$)/, '-side.png$1');
        var c = FRAME_EDGE[frame] || FRAME_EDGE.black;
        side.style.backgroundImage = sideSrc
          ? feather(right, horizontal) + ', url("' + sideSrc + '")'
          : feather(right, horizontal) + ', var(--pp-wood-grain)';
        // Stretched to the face either way: the strip's long axis is the run of
        // the grain, so on a top or bottom rail it lies along the rail, which is
        // how the wood is actually cut.
        side.style.backgroundSize = '100% 100%, 100% 100%';
        side.style.backgroundPosition = '0 0, center';
        side.style.backgroundRepeat = 'no-repeat, no-repeat';
        side.style.backgroundBlendMode = 'normal, normal';
        side.style.backgroundColor = c[0];
        return;
      }

      if (bleeds && src && src.indexOf('R0lGODlh') === -1) {
        side.style.backgroundImage =
          feather(right, horizontal) + ', var(--pp-canvas-weave-light), url("' + src + '")';
        side.style.backgroundSize = '100% 100%, var(--pp-canvas-weave-size, 22px), ' +
          (horizontal ? '100% auto' : 'auto 100%');
        side.style.backgroundPosition = '0 0, 0 0, ' + (horizontal
          ? 'center ' + (right ? '100%' : '0%')
          : (right ? '100%' : '0%') + ' center');
        side.style.backgroundRepeat = 'no-repeat, repeat, no-repeat';
        side.style.backgroundBlendMode = 'normal, multiply, normal';
      } else {
        // Bordered design: the sheet runs out to the edge in the background
        // colour, so the wrap is that colour — with the weave over it, because
        // the side is cloth as much as the face is.
        side.style.backgroundImage = feather(right, horizontal) + ', var(--pp-canvas-weave-light)';
        side.style.backgroundSize = '100% 100%, var(--pp-canvas-weave-size, 22px)';
        side.style.backgroundPosition = '0 0, 0 0';
        side.style.backgroundRepeat = 'no-repeat, repeat';
        side.style.backgroundBlendMode = 'normal, multiply';
        side.style.backgroundColor = bg && bg !== 'rgba(0, 0, 0, 0)' ? bg : '#ffffff';
      }
    });
    measureOverhang();
  }

  // The preview is the same size on screen whatever canvas is chosen, so a
  // millimetre is worth fewer pixels on a big one. Everything physical here —
  // the depth of the edge, the width of the moulding — goes through this.
  function pxPerMm() {
    var sizeRadio = document.querySelector('.bp-variant-options-radio:checked');
    var widthCm = parseFloat(((sizeRadio && sizeRadio.value) || '30x40').split('x')[0]) || 30;
    return front.getBoundingClientRect().width / (widthCm * 10);
  }

  // The moulding's front face is 9mm. The asset carries that as 19 of its own
  // pixels inside a 27px band, the rest being the float gap, so the border the
  // nine-slice is drawn at is 9mm scaled by that ratio.
  var FRONT_MM = 9, SRC_BAND = 27, SRC_WOOD = 19;

  // What each frame is made of, sampled from its own asset: the face of the
  // moulding, and the float gap beside it. These paint the ring behind the
  // frame that keeps a border-image seam from showing the page through it.
  var FRAME_FILL = {
    'oak':      ['#d4b495', '#46301f'],
    'dark-oak': ['#744e39', '#170e09'],
    'black':    ['#353636', '#060706'],
    'white':    ['#f0f0ef', '#59544f'],
  };

  function frameBandPx(frame) {
    return frame ? FRONT_MM * (SRC_BAND / SRC_WOOD) * pxPerMm() : 0;
  }

  function depthPx() {
    // Those millimetres in the preview's own scale, so the edge is as deep in
    // the picture as it is on the wall.
    var frame = currentFrame();
    var mm = frame ? BAR_MM.framed : BAR_MM.unframed;
    var w = front.getBoundingClientRect().width;
    var px = mm * pxPerMm();
    // A guard against a preview measured mid-layout, not a design limit. The
    // old ceiling of 44px was one: 45mm on a 30x40 is 74px, and a flat 44 both
    // flattened the frame and quietly made a 30x40 and a 50x70 the same depth,
    // when the whole point is that the smaller canvas carries the same frame
    // and so looks deeper.
    return Math.max(6, Math.min(w * 0.25, px));
  }

  // The frame layer is drawn LARGER than the canvas it surrounds — about ten
  // pixels proud on every side — so the outer edge of the moulding is not the
  // edge of .bp-poster-preview. Sides pinned to the preview therefore started
  // ten pixels inside the frame, which is why the depth looked like it belonged
  // to something behind the frame rather than to the frame itself. Measure the
  // overhang and push the faces out to meet it.
  // Measured from offsetWidth/offsetHeight, which are the element's own
  // untransformed border box and so are blind to the turn. The version before
  // this measured client rects, which are projections under a rotation and
  // therefore wrong, and worked around that by taking the transform off the
  // container and putting it back — on every frame of every animation, which
  // cancelled the animation. That is what made the turn crawl, drop its sides
  // and stop short of square.
  //
  // The frame layer is centred on the canvas and drawn a few pixels proud of
  // it on every side, so the overhang is simply half the difference. Without
  // it the faces start inside the moulding and the depth looks like it belongs
  // to something behind the frame.
  function measureOverhang() {
    var x = 0, y = 0;
    var layer = currentFrame() && cont.querySelector('.bp-poster-frame');
    if (layer && layer.offsetWidth && front.offsetWidth) {
      // Exactly the overhang, so the faces hinge on the frame's outer edge and
      // the tip of a side meets the tip of the corner. This used to take a
      // pixel off, as insurance against a sub-pixel gap letting the page
      // through at the hinge; the ring behind the frame does that job properly
      // now, and the pixel showed as a step where the two tips should meet.
      x = Math.max(0, Math.round((layer.offsetWidth - front.offsetWidth) / 2));
      y = Math.max(0, Math.round((layer.offsetHeight - front.offsetHeight) / 2));
    }
    cont.style.setProperty('--pp-over-top', y + 'px');
    cont.style.setProperty('--pp-over-bottom', y + 'px');
    cont.style.setProperty('--pp-over-left', x + 'px');
    cont.style.setProperty('--pp-over-right', x + 'px');
  }

  function render() {
    var d = depthPx();
    cont.style.setProperty('--pp-tilt', tilt.toFixed(2) + 'deg');
    cont.style.setProperty('--pp-depth', d.toFixed(1) + 'px');


    // Only the edge that is actually facing you is drawn, so you never catch a
    // glimpse of the far side through the front face.
    sideRight.style.opacity = tilt < -1 ? '1' : '0';
    sideLeft.style.opacity = tilt > 1 ? '1' : '0';
    // Top and bottom earn their keep only in the middle of the turn.
    //
    // Head-on you see the front of a frame and nothing else. These two stand
    // perpendicular to the screen, but perspective still projects them as a
    // band — 18px of it once the depth became a true 45mm — and that band lands
    // on the frame's own rails, painted with a different picture than the rail
    // underneath. That is the line across the top and bottom at rest.
    //
    // Once the canvas has turned enough for a side to have width, the corners
    // would read as an open tube without them. Past twelve degrees the side
    // covers the corner itself, and a rectangular face against a mitred rail
    // starts to show as a step. So: nothing at rest, in by five degrees, gone
    // by twelve.
    var a = Math.abs(tilt);
    var corner = Math.min(1, Math.max(0, (a - 1.5) / 3.5)) * Math.max(0, 1 - a / 12);
    if (sideTop) sideTop.style.opacity = corner.toFixed(2);
    if (sideBottom) sideBottom.style.opacity = corner.toFixed(2);
  }

  function setTilt(v, animate, ms) {
    tilt = Math.max(-MAX_TILT, Math.min(MAX_TILT, v));
    cont.style.transition = animate
      ? 'transform ' + (ms || 550) + 'ms cubic-bezier(.33,.0,.2,1)'
      : 'none';
    // The faces fade over the same span as the turn. Dragging, the tilt follows
    // the finger with no transition at all, so the face appears instantly.
    cont.style.setProperty('--pp-face-ms', (animate ? (ms || 550) : 0) + 'ms');
    render();
  }

  // A hand, moved slowly across the poster while the canvas follows it. The
  // tilt alone told nobody the thing could be touched — a product photo that
  // moves on its own reads as a loading animation, not an invitation. The hand
  // makes the gesture explicit.
  //
  // Held back until the artwork is actually on screen: demonstrating a drag over
  // a half-loaded poster wastes the one moment anyone is watching.
  function artworkReady() {
    var img = document.querySelector('.petposter-template .bp-illustration-img');
    var src = img && img.getAttribute('src');
    if (!src || src.indexOf('R0lGODlh') !== -1) return false;   // the 1×1 placeholder
    return !!(img.complete && img.naturalWidth);
  }

  // One pending wait at a time. Every call used to start its own poller, and
  // they all survived to call demo() — one click produced four hands, because
  // arms left over from the page load and the material switch were still
  // counting down behind this one.
  var pendingPoll = 0;

  function whenArtworkReady(cb) {
    if (pendingPoll) { clearInterval(pendingPoll); pendingPoll = 0; }
    var fired = false, poll, waited = 0;
    var go = function () {
      if (fired) return;
      fired = true;
      clearInterval(poll);
      if (pendingPoll === poll) pendingPoll = 0;
      setTimeout(cb, 500);
    };
    if (artworkReady()) return go();
    // Switching material swaps the artwork for a different crop of it, and the
    // old picture is still `complete` while the new one loads — waiting on a
    // load event alone demonstrated the drag over whatever was on its way out.
    // Poll for a picture that is actually there instead.
    poll = pendingPoll = setInterval(function () {
      waited += 200;
      if (artworkReady()) return go();
      // Hold on for a picture that is on its way. Hold much less for a preview
      // with nothing in it at all — on a product page where no portrait has
      // been made yet, the hint should not sit waiting for one that may never
      // come.
      var img = document.querySelector('.petposter-template .bp-illustration-img');
      var src = img && img.getAttribute('src');
      var coming = !!(src && src.indexOf('R0lGODlh') === -1);
      if (waited >= (coming ? 8000 : 1800)) go();
    }, 200);
  }

  function addZoomButton() {
    if (cont.querySelector('.pp-zoom-btn')) return;
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'pp-zoom-btn';
    b.setAttribute('aria-label', 'Zoom');
    b.innerHTML = '<svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
      'stroke-width="2" stroke-linecap="round"><circle cx="10.5" cy="10.5" r="6.5"/>' +
      '<line x1="15.5" y1="15.5" x2="21" y2="21"/></svg>';
    b.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      // The zoom handler ignores clicks whose target is a button, so hand it a
      // click on the container instead of relying on this one bubbling.
      cont.dispatchEvent(new MouseEvent('click', { bubbles: true }));
    });
    // Outside the stage, not in it. Anything inside takes every transform the
    // canvas takes — the button swung with the turn and leaned with the
    // perspective, and counter-rotating it only kept it facing the viewer
    // rather than still. Its corner is measured instead, below.
    var host = cont.parentElement;
    if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
    host.appendChild(b);
    placeZoomButton();
    // The preview's height is an aspect-ratio padding, and it settles AFTER
    // this first placement — the button was being positioned for a 50x70 box
    // and then left there while the box became a 30x40, which put it astride
    // the bottom edge. Watch the box instead of guessing when it is final.
    if (typeof ResizeObserver !== 'undefined') {
      new ResizeObserver(placeZoomButton).observe(cont);
    }
  }

  // Inside the picture, at its bottom-right corner, in the host's coordinates.
  //
  // It used to sit under the preview, which put it adrift in the line of text
  // and attached to nothing. On the artwork it reads as what it is — the way to
  // look closer at THIS — and it is in the same place at every screen size.
  // Inset past the moulding so it sits on the canvas rather than on the wood,
  // and clear of the name, which is centred.
  function placeZoomButton() {
    var host = cont.parentElement;
    var b = host && host.querySelector(':scope > .pp-zoom-btn');
    if (!b) return;
    // From the LAYOUT box, never from a client rect. A client rect is the
    // bounding box of the turned quad, it moves with the turn, and the turn
    // runs on a transition — so `tilt` is back to zero while the canvas is
    // still visibly leaning, and a rect read in that window placed the button
    // thirty pixels low, half off the corner. offsetLeft and friends are the
    // untransformed box: the same answer whatever the canvas is doing.
    //
    // OUTSIDE the picture, below the frame's bottom-right corner and flush
    // with its right edge. On the artwork it covered the thing it is there to
    // let you look at. The moulding stands proud of the canvas, so the frame's
    // own edges are the canvas plus the band.
    var band = frameBandPx(currentFrame());
    var gap = 10;
    var size = b.offsetWidth || 40;
    b.style.right = 'auto';
    b.style.bottom = 'auto';
    b.style.left = Math.round(cont.offsetLeft + cont.offsetWidth + band - size) + 'px';
    b.style.top = Math.round(cont.offsetTop + cont.offsetHeight + band + gap) + 'px';

    // The size and material line lives in that same strip, centred. On a wide
    // screen it stops well short of the button; on a narrow one it would run
    // underneath it. Measure rather than assume, and drop the button below the
    // line when they would meet. Both of these are untransformed, so a client
    // rect is honest here in a way it is not for the canvas.
    var cap = document.querySelector('.bp-poster-cont .bp-poster-bottom-subheading');
    if (cap) {
      var textRight = 0;
      cap.querySelectorAll('span').forEach(function (sp) {
        textRight = Math.max(textRight, sp.getBoundingClientRect().right);
      });
      var br = b.getBoundingClientRect(), cr = cap.getBoundingClientRect(), hr = host.getBoundingClientRect();
      if (textRight > br.left - 10 && br.top < cr.bottom && br.bottom > cr.top) {
        b.style.top = Math.round(cr.bottom - hr.top + 10) + 'px';
      }
    }
  }

  function hand() {
    var h = cont.querySelector('.pp-drag-hand');
    if (h) return h;
    h = document.createElement('div');
    h.className = 'pp-drag-hand';
    h.setAttribute('aria-hidden', 'true');
    h.innerHTML = '<svg viewBox="0 0 24 24" width="44" height="44" fill="none" ' +
      'stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">' +
      '<path d="M9 11V5.5a1.5 1.5 0 0 1 3 0V11"/>' +
      '<path d="M12 11V4.5a1.5 1.5 0 0 1 3 0V11"/>' +
      '<path d="M15 11.5V6.5a1.5 1.5 0 0 1 3 0V14"/>' +
      '<path d="M9 11V9a1.5 1.5 0 0 0-3 0v6.5c0 3 2.4 5.5 5.5 5.5h2A4.5 4.5 0 0 0 18 16.5V14"/>' +
      '</svg>';
    // The gesture on its own is ambiguous — a hand sliding about could mean
    // swipe to the next image. The words say what it does, in whatever language
    // the customer is reading.
    var hint = (window.ppMaterialData && window.ppMaterialData.dragHint) || '';
    if (hint) {
      var label = document.createElement('span');
      label.className = 'pp-drag-hand__label';
      label.textContent = hint;
      h.appendChild(label);
    }
    cont.appendChild(h);
    return h;
  }

  // The demo owns its timers and runs once per switch into canvas — not once
  // per page. Choosing CANVAS is the moment the hint is worth something, and
  // the switch happens in place, without a reload, so a page-lifetime guard
  // meant everyone but the people who landed on canvas never saw it.
  //
  // The guard that remains is against overlap: pp:material-changed fires on
  // load as well as on a real switch, and two demos running over each other
  // cut each other short.
  var demoBusy = false;
  var demoTimers = [];

  function demoStop() {
    demoTimers.forEach(clearTimeout);
    demoTimers = [];
    demoBusy = false;
    var h = cont && cont.querySelector('.pp-drag-hand');
    if (h && h.parentNode) h.parentNode.removeChild(h);
  }

  function demo() {
    if (demoBusy || dragging) return;   // never over a hand that is already there
    demoBusy = true;
    paintEdges();
    var h = hand();
    var at = function (ms, fn) { demoTimers.push(setTimeout(fn, ms)); };
    var move = function (x, ms) {
      h.style.transition = 'transform ' + ms + 'ms cubic-bezier(.33,0,.2,1), opacity .45s ease';
      h.style.transform = 'translate(-50%, -50%) translateX(' + x + 'px)';
    };
    // The browser needs the element to have been laid out at opacity 0 before
    // the class change can transition — without the frame in between it jumps
    // straight to the end state, which here meant never appearing at all.
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { h.classList.add('is-on'); });
    });
    at(900,  function () { move(78, 1200); setTilt(30, true, 1200); });
    at(2300, function () { move(-78, 1800); setTilt(-30, true, 1800); });
    at(4300, function () { move(0, 700); setTilt(REST_TILT, true, 700); });
    at(5100, function () { h.classList.remove('is-on'); });
    at(5800, demoStop);
  }

  function bind() {
    // A pointer can report several moves inside one frame, and each one used to
    // write a transform that was never painted. The turn is now written once a
    // frame, off the latest position — which is the one the eye gets anyway.
    var lastX = 0, frame = 0;
    var apply = function () {
      frame = 0;
      if (!dragging) return;
      setTilt(startTilt + (lastX - startX) * 0.28, false);
    };
    var down = function (e) {
      demoStop();                  // the demo must not fight the hand that arrived
      dragging = true;
      moved = 0;
      startX = lastX = (e.touches ? e.touches[0].clientX : e.clientX);
      startTilt = tilt;
      cont.classList.add('pp-tiltable--grabbing');
    };
    var move = function (e) {
      if (!dragging) return;
      lastX = (e.touches ? e.touches[0].clientX : e.clientX);
      moved = Math.max(moved, Math.abs(lastX - startX));
      if (moved > DRAG_SLOP && e.cancelable) e.preventDefault();
      if (!frame) frame = requestAnimationFrame(apply);
    };
    var up = function () {
      if (!dragging) return;
      dragging = false;
      // A frame still queued would land after this and set the tilt again with
      // no transition, which killed the settle on the way home and left the
      // canvas standing wherever the finger let go.
      if (frame) { cancelAnimationFrame(frame); frame = 0; }
      cont.classList.remove('pp-tiltable--grabbing');
      setTilt(REST_TILT, true, 420);  // always settles back, so it cannot be left facing away
      // Cleared after the click this release may produce. A drag that ended off
      // the poster produces no click at all, and the leftover distance then ate
      // the next genuine one — the zoom button ignoring your first press.
      setTimeout(function () { moved = 0; }, 0);
    };
    cont.addEventListener('click', function (e) {
      if (moved > DRAG_SLOP) { e.stopPropagation(); e.preventDefault(); moved = 0; }
    }, true);
    cont.addEventListener('mousedown', down);
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    cont.addEventListener('touchstart', down, { passive: true });
    window.addEventListener('touchmove', move, { passive: false });
    window.addEventListener('touchend', up);

    // Putting a frame on is the other moment worth demonstrating the turn. The
    // side is what changed most — a moulding just appeared on it — and the
    // customer is looking straight at the preview, having just scrolled to it.
    //
    // Only on a real tap. These radios are set programmatically all over the
    // place: the material switch picks the default frame, the saved-design
    // restore clicks one, and the page-load pass fires change on whatever is
    // checked. A hand sliding across the poster on page load would be noise.
    var replayTimer = 0;
    document.addEventListener('change', function (e) {
      if (!active || !e.isTrusted) return;
      if (!e.target || !e.target.classList || !e.target.classList.contains('bp-frame-radio')) return;
      // Debounced, and it will not cut in on a demo already playing. Flicking
      // through four frames should leave one hand behind, not four.
      if (replayTimer) clearTimeout(replayTimer);
      replayTimer = setTimeout(function () {
        replayTimer = 0;
        if (demoBusy) return;
        whenArtworkReady(demo);
      }, 260);
    });

    // Anything that changes what reaches the edge of the sheet has to repaint
    // it. Frame and size were obvious; fill-poster was not, and missing it left
    // a white wrap on a design bleeding dark paint right off the edge.
    var queued = 0;
    var repaint = function () {
      if (queued) return;
      queued = setTimeout(function () {
        queued = 0;
        if (dragging) return;      // never repaint under the finger
        paintEdges();
        render();
        measureOverhang();
      }, 60);
    };
    // Depth and overhang are both in pixels, derived from how wide the preview
    // is drawn — so a rotated phone has to have them worked out again.
    window.addEventListener('resize', function () { repaint(); placeZoomButton(); });
    document.addEventListener('change', function (e) {
      if (!e.target || !e.target.classList) return;
      if (e.target.classList.contains('bp-frame-radio') ||
          e.target.classList.contains('bp-variant-options-radio') ||
          e.target.id === 'bp-fill-poster-toggle') repaint();
    });

    // The background colour and fill both land as class/style changes on the
    // template, and a new portrait swaps the image src. Watch rather than guess
    // at the full list of things that can cause it.
    //
    // Watch those two elements ONLY, never the subtree. Everything this file
    // writes — the tilt on the container, the four faces — lives inside that
    // subtree, so watching it meant every paint scheduled another paint sixty
    // milliseconds later, for as long as the page was open. That loop repainted
    // four background stacks and remeasured the frame on top of whatever turn
    // was in flight: the stutter, the lag, the sides blinking out and the
    // canvas never quite arriving home were all it.
    if (typeof MutationObserver !== 'undefined') {
      var tpl = document.querySelector('.petposter-template');
      if (tpl) new MutationObserver(repaint).observe(tpl, {
        attributes: true, attributeFilter: ['class', 'style'],
      });
      // The background colour lands here, and we never write to this element.
      new MutationObserver(repaint).observe(front, {
        attributes: true, attributeFilter: ['class', 'style'],
      });
      var img = document.querySelector('.petposter-template .bp-illustration-img');
      if (img) new MutationObserver(repaint).observe(img, { attributes: true, attributeFilter: ['src'] });
    }
  }

  function enabled(on) {
    if (!cont && !mount()) return;
    active = !!on;
    cont.classList.toggle('pp-tiltable', on);
    var zoom = cont.parentElement && cont.parentElement.querySelector(':scope > .pp-zoom-btn');
    if (zoom) zoom.classList.toggle('is-on', !!on);
    if (on) { paintEdges(true); setTilt(REST_TILT, true); whenArtworkReady(demo); }
    else {
      demoStop();
      setTilt(0, true);
      lastRing = '';
      lastRingAsWritten = null;
      front.style.boxShadow = '';
      cont.style.marginBottom = '';
      // Leave nothing of the frame behind on a poster, even values nothing is
      // reading today.
      cont.style.removeProperty('--pp-frame-band');
      cont.style.removeProperty('--pp-frame-src');
      lastPaint = null;
    }
  }

  function start() {
    if (!mount()) return;
    var isCanvas = document.querySelector('.petposter-template').classList.contains('canvas-poster');
    enabled(isCanvas);
    document.addEventListener('pp:material-changed', function (e) {
      enabled(!!(e.detail && e.detail.material === 'canvas'));
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  setTimeout(start, 900);   // the preview is built late on some paths
})();
